import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';
import { extractErrorMessage } from '../../utils/errorHandler';
import axios from 'axios';
import API_CONFIG from '../../config/apiConfig';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  // Check if token exists
  React.useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const handleChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    setError(null);
  };

  const handleSubmit = async (event?: React.FormEvent | React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    if (!formData.password || !formData.confirmPassword) {
      setError('Please enter and confirm your new password');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const authServiceUrl = API_CONFIG.AUTH_SERVICE_URL || API_CONFIG.BASE_URL;
      const resetUrl = `${authServiceUrl}/api/v1/auth/password/reset/complete`;

      await axios.post(resetUrl, {
        token: token,
        new_password: formData.password,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout
      });

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="xs">
        <div style={{ marginTop: 64, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', backgroundColor: '#fff' }}>
            <CheckCircleIcon style={{ fontSize: 60, color: '#2e7d32', marginBottom: 16 }} />
            <Typography component="h1" variant="h5" style={{ marginBottom: 16, fontWeight: 'bold' }}>
              Password Reset Successful
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" style={{ marginBottom: 24 }}>
              Your password has been reset successfully. You can now log in with your new password.
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" style={{ marginBottom: 24 }}>
              Redirecting to login page...
            </Typography>
            <div style={{ textAlign: 'center', width: '100%' }}>
              <Link component={RouterLink} to="/login" variant="body1" style={{ fontWeight: 500 }}>
                Go to Login
              </Link>
            </div>
          </Paper>
        </div>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <div style={{ marginTop: 64, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', backgroundColor: '#fff' }}>
          <Typography
            component="h1"
            variant="h4"
            style={{
              marginBottom: 8,
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #325BA8 30%, #4DC4D2 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Prismatic Forge
          </Typography>

          <Typography variant="h6" style={{ marginBottom: 8 }}>
            Create New Password
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" style={{ marginBottom: 24 }}>
            Enter your new password below.
          </Typography>

          {error && (
            <Alert severity="error" style={{ width: '100%', marginBottom: 16 }}>
              {error}
            </Alert>
          )}

          <form noValidate style={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              autoFocus
              value={formData.password}
              onChange={handleChange('password')}
              disabled={loading || !token}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              disabled={loading || !token}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              style={{ marginTop: 24, marginBottom: 16, paddingTop: 12, paddingBottom: 12 }}
              disabled={loading || !token}
              onClick={handleSubmit}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Reset Password'
              )}
            </Button>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to="/login"
                variant="body2"
                color="text.secondary"
              >
                Back to Login
              </Link>
            </div>
          </form>
        </Paper>
      </div>
    </Container>
  );
};

export default ResetPasswordPage;
